import { useState } from 'react';
import { parseMoneyInput } from '@/lib/subscriptionPlans';
import type { BesoinCategorie, BesoinInput } from '@/lib/besoins';

interface Props {
  categories: BesoinCategorie[];
  monaie: string;
  onClose: () => void;
  onSubmit: (input: BesoinInput) => Promise<void>;
}

const CURRENCIES = ['MAD', 'EUR', 'USD', 'XOF', 'XAF'];

/** Création d'un besoin : titre, description courte, catégorie, zone, budget. */
export default function CreateBesoinModal({ categories, monaie, onClose, onSubmit }: Props) {
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [categorieId, setCategorieId] = useState<string>(categories[0] ? String(categories[0].id) : '');
  const [ville, setVille] = useState('');
  const [budgetText, setBudgetText] = useState('');
  const [devise, setDevise] = useState(CURRENCIES.includes(monaie) ? monaie : 'MAD');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!titre.trim()) {
      setError('Donnez un titre à votre besoin.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const matched = categories.find((c) => String(c.id) === categorieId);
      await onSubmit({
        titre: titre.trim(),
        description: description.trim(),
        categorie: matched?.nom || '',
        categorieId: matched ? matched.id : null,
        ville: ville.trim(),
        budget: budgetText.trim() ? parseMoneyInput(budgetText) : null,
        monaie: devise,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de créer le besoin.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background-50 rounded-xl border border-background-200/70">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
          <div>
            <h3 className="text-base font-bold font-heading text-foreground-950">Créer un besoin</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Décrivez ce qu'il vous faut, les pros concernés seront notifiés.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre du besoin <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="ex. Livreur pour mes commandes du week-end"
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Description courte</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="Ce dont vous avez besoin, quand, où..."
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
            />
            <p className="text-[11px] text-foreground-400 mt-1 text-right">{description.length}/500</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Catégorie de professionnel</label>
              <select
                value={categorieId}
                onChange={(e) => setCategorieId(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">Tous les professionnels</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Ville / zone</label>
              <input
                type="text"
                value={ville}
                onChange={(e) => setVille(e.target.value)}
                placeholder="ex. Brazzaville"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Prix que vous proposez</label>
              <input
                type="text"
                inputMode="decimal"
                value={budgetText}
                onChange={(e) => setBudgetText(e.target.value)}
                placeholder="ex. 0,5 ou 5000"
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Devise</label>
              <select
                value={devise}
                onChange={(e) => setDevise(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-[11px] text-foreground-400">
            Ce prix sera visible par tous les professionnels : ceux qui l'acceptent peuvent prendre la mission directement. Laissez vide pour laisser les pros proposer leur prix.
          </p>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              {error}
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-background-200/70">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
            Publier le besoin
          </button>
        </div>
      </div>
    </div>
  );
}